import {
	getQueryStringFromObj,
	joinWithSlashes,
} from "components/ProjectNonSpecificComponents/API/Requests/utils"
import { IRequestsConfig } from "../../../ProjectNonSpecificComponents/API/Requests/Interfaces"
import Requests from "../../../ProjectNonSpecificComponents/API/Requests/Requests"
import {
	IRequestOptions,
	TAPIBranchName,
	TApplicablePaths_DELETE_Global,
	TApplicablePaths_GET_Global,
	TApplicablePaths_POST_Global,
	TApplicablePaths_PUT_Global,
	TAppRequestMessageIds,
} from "./Interfaces"

/**
 * Short-hand tool for developer experience,
 * and the main function through which to use project-specific API.
 */
const AppRequests = (APIBranchName: TAPIBranchName, config?: Partial<IRequestsConfig>) => {
	let requestsConfig = Object.assign(defaultRequestsConfig, config ?? {})
	const requests = new Requests<TAppRequestMessageIds>(requestsConfig)

	const basePath = "ui/"

	return {
		get<IData>(path?: TApplicablePaths_GET_Global, requestOptions?: IRequestOptions) {
			let {
				domain,
				withCredentials,
				isResponseBlobType,
				query,
				params = "",
				id,
				requestedItemId,
				formsConfigId,
				itemOfTypeId,
				filterFieldId,
			} = requestOptions || {}

			const url = joinWithSlashes(
				domain,
				basePath,
				APIBranchName,
				path,
				id,
				requestedItemId,
				formsConfigId,
				itemOfTypeId,
				filterFieldId,
				query ? getQueryStringFromObj(query) : undefined
			)

			return requests.get<IData>(`${url}${!!params ? params : ""}`, {
				responseType: isResponseBlobType ? "blob" : undefined,
				// Always true, unless specified otherewise
				withCredentials: withCredentials != null ? withCredentials : true,
			})
		},
		post<IBody, IReturnData = unknown>(
			path: TApplicablePaths_POST_Global | null,
			body: IBody,
			requestOptions?: IRequestOptions
		) {
			let {
				domain,
				isResponseBlobType,
				query, // isn't used in POST
				requestedItemId,
				formsConfigId,
				withCredentials,
			} = requestOptions || {}

			const url = joinWithSlashes(
				domain,
				basePath,
				APIBranchName,
				path ?? undefined,
				requestedItemId,
				formsConfigId
			)

			return requests.post<IBody, IReturnData>(url, body, {
				responseType: isResponseBlobType ? "blob" : undefined,
				// Always true, unless specified otherewise
				withCredentials: withCredentials != null ? withCredentials : true,
			})
		},
		put<IBody, IReturnData = unknown>(
			path: TApplicablePaths_PUT_Global | null,
			body: IBody,
			requestOptions?: IRequestOptions
		) {
			let { domain, isResponseBlobType, requestedItemId, formsConfigId, withCredentials } =
				requestOptions || {}

			const url = joinWithSlashes(
				domain,
				basePath,
				APIBranchName,
				path ?? undefined,
				requestedItemId,
				formsConfigId
			)

			return requests.put<IBody, IReturnData>(url, body, {
				responseType: isResponseBlobType ? "blob" : undefined,
				// Always true, unless specified otherewise
				withCredentials: withCredentials != null ? withCredentials : true,
			})
		},
		delete<IReturnData = unknown>(
			path: TApplicablePaths_DELETE_Global | null,
			requestOptions?: IRequestOptions
		) {
			let { domain, isResponseBlobType, requestedItemId, formsConfigId, withCredentials } =
				requestOptions || {}

			const url = joinWithSlashes(
				domain,
				basePath,
				APIBranchName,
				path ?? undefined,
				requestedItemId,
				formsConfigId
			)

			return requests.delete<IReturnData>(url, {
				responseType: isResponseBlobType ? "blob" : undefined,
				// Always true, unless specified otherewise
				withCredentials: withCredentials != null ? withCredentials : true,
			})
		},
	}
}

export default AppRequests

type TApi_Path =
	| "Collection"
	| "Connection"
	| "Requests"
	| "ItemType"
	| "RequestForms"
	| "Setting"
	| "Tool"
	| "User"
	| "UserActive"
	| "Admin"
	| "VersionNumber"

const defaultRequestsConfig: IRequestsConfig = {
	language: "en",
	specificUserFacingMessages: {
		en: undefined,
		lv: undefined,
	},
}
